/**
 * Avatar image cache to avoid repeated requests to external URLs (e.g. Google profile photos).
 * Fetches each URL once, converts to a blob URL, and reuses it to prevent 429 (Too Many Requests).
 * On 429 from a Google user icon URL only, records 24h backoff via googleRateLimit.
 */
import { record429, isInBackoff } from './googleRateLimit';

const MAX_CACHE_SIZE = 80;
const cache = new Map(); // url -> { blobUrl, ts }
const inFlight = new Map(); // url -> Promise<blobUrl>

/** True if URL is a Google user profile/icon URL (e.g. lh3.googleusercontent.com). */
function isGoogleUserPhotoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url.trim());
    return /^lh[0-9]*\.googleusercontent\.com$/i.test(u.hostname) || u.hostname === 'googleusercontent.com';
  } catch {
    return false;
  }
}

function isExternalPhotoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return (trimmed.startsWith('http://') || trimmed.startsWith('https://')) && !trimmed.startsWith(window.location.origin);
}

function evictOldest() {
  if (cache.size <= MAX_CACHE_SIZE) return;
  let oldestUrl = null;
  let oldestTs = Infinity;
  for (const [u, entry] of cache) {
    if (entry.ts < oldestTs) {
      oldestTs = entry.ts;
      oldestUrl = u;
    }
  }
  if (oldestUrl) {
    const entry = cache.get(oldestUrl);
    if (entry?.blobUrl) try { URL.revokeObjectURL(entry.blobUrl); } catch (_) {}
    cache.delete(oldestUrl);
  }
}

/**
 * Returns a cached blob URL for the given photo URL, or fetches it once and caches.
 * For data: or same-origin URLs, returns the URL as-is (no fetch).
 * Deduplicates in-flight requests for the same URL.
 * @param {string|null|undefined} url - Photo URL (e.g. Google profile image)
 * @returns {Promise<string|null>} Resolved with blob URL or original URL; null if url is falsy
 */
export function getCachedAvatarUrl(url) {
  if (!url || typeof url !== 'string') return Promise.resolve(null);
  const trimmed = url.trim();
  if (!trimmed) return Promise.resolve(null);
  // data: and same-origin: use as-is to avoid unnecessary fetch
  if (trimmed.startsWith('data:')) return Promise.resolve(trimmed);
  if (trimmed.startsWith(window.location.origin) || trimmed.startsWith('/')) return Promise.resolve(trimmed);
  // Already cached
  const hit = cache.get(trimmed);
  if (hit) return Promise.resolve(hit.blobUrl);
  // Skip fetching Google user icon URLs while in 429 backoff
  if (isGoogleUserPhotoUrl(trimmed) && isInBackoff()) return Promise.resolve(null);
  // In-flight: reuse same promise
  const pending = inFlight.get(trimmed);
  if (pending) return pending;

  const promise = fetch(trimmed, { mode: 'cors', credentials: 'omit' })
    .then((res) => {
      if (res.status === 429) {
        if (isGoogleUserPhotoUrl(trimmed)) record429();
        throw new Error('Too many requests (429). Try again in 24 hours.');
      }
      if (!res.ok) throw new Error(`Avatar fetch ${res.status}`);
      return res.blob();
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      cache.set(trimmed, { blobUrl, ts: Date.now() });
      evictOldest();
      return blobUrl;
    })
    .catch(() => {
      inFlight.delete(trimmed);
      return null; // on 429 / unavailable, return null so UI can show initials fallback
    })
    .then((result) => {
      inFlight.delete(trimmed);
      return result;
    });

  inFlight.set(trimmed, promise);
  return promise;
}

/**
 * Synchronous check: returns cached blob URL if available, otherwise null.
 * Use this to avoid flash when cache is already warm.
 * @param {string|null|undefined} url
 * @returns {string|null}
 */
export function getCachedAvatarUrlSync(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (trimmed.startsWith('data:') || trimmed.startsWith(window.location.origin) || trimmed.startsWith('/')) return trimmed;
  const hit = cache.get(trimmed);
  return hit ? hit.blobUrl : null;
}

export function isExternalPhotoUrlExport(url) {
  return isExternalPhotoUrl(url);
}
